import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const getScopeLabel = (scope, officeName = "") => {
    if (scope === "office") {
        return officeName ? `Офис · ${officeName}` : "Офис";
    }

    return "Личное";
};

const getTypeLabel = (type, meta = {}) => {
    const event = meta.event || "";

    if (type === "chat" || event === "chat_message") return "Сообщение";
    if (type === "order" || event.includes("order")) return "Заказ";
    if (type === "payment" || type === "billing") return "Биллинг";
    if (type === "inventory" || event.includes("inventory") || event.includes("warehouse")) return "Склад";
    if (type === "system") return "Система";

    return "Уведомление";
};

const normalizeType = (type, meta = {}) => {
    const event = meta.event || "";

    if (type === "chat" || event === "chat_message") return "chat";
    if (type === "payment" || type === "billing") return "billing";
    if (type === "order" || event.includes("order")) return "order";
    if (type === "inventory" || event.includes("inventory") || event.includes("warehouse")) return "inventory";
    if (type === "system") return "system";

    return "other";
};

const openExternalOrNavigate = (navigate, link) => {
    if (!link) return;

    if (/^https?:\/\//i.test(link)) {
        window.location.href = link;
        return;
    }

    navigate(link);
};

const NotificationToast = ({ data, onClose }) => {
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);

    const notification = useMemo(() => {
        if (!data || typeof data !== "object") return null;

        return {
            title: data.title || "Уведомление",
            message: data.message || "",
            createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
            link: data.link || "",
            scope: data.scope || data.meta?.scope || "personal",
            type: data.type || "system",
            meta: data.meta || {},
            officeName: String(
                data.office?.name ||
                data.meta?.officeName ||
                ""
            ).trim(),
        };
    }, [data]);

    useEffect(() => {
        if (!notification) {
            onClose?.();
            return undefined;
        }

        const openTimer = setTimeout(() => {
            setVisible(true);
        }, 30);

        const closeTimer = setTimeout(() => {
            setVisible(false);

            setTimeout(() => {
                onClose?.();
            }, 300);
        }, 8000);

        return () => {
            clearTimeout(openTimer);
            clearTimeout(closeTimer);
        };
    }, [notification, onClose]);

    if (!notification) return null;

    const handleClick = () => {
        if (notification.link) {
            openExternalOrNavigate(navigate, notification.link);
            onClose?.();
            return;
        }

        setVisible(false);

        setTimeout(() => {
            onClose?.();
        }, 300);
    };

    const handleClose = (event) => {
        event.stopPropagation();

        setVisible(false);

        setTimeout(() => {
            onClose?.();
        }, 300);
    };

    const typeClass = normalizeType(notification.type, notification.meta);

    return (
        <div
            className={[
                "notification-toast-AO",
                `notification-toast-AO--${notification.scope}`,
                `notification-toast-AO--type-${typeClass}`,
                visible ? "is-visible" : "is-hidden",
            ].filter(Boolean).join(" ")}
            onClick={handleClick}
            role="status"
            aria-live="polite"
        >
            <div className="notification-toast-AO__header">
                <div className="notification-toast-AO__head-main">
                    <span className="notification-toast-AO__badge">
                        {getScopeLabel(notification.scope, notification.officeName)} · {getTypeLabel(notification.type, notification.meta)}
                    </span>

                    <strong>{notification.title}</strong>
                </div>

                <div className="notification-toast-AO__side">
                    <span className="notification-toast-AO__time">
                        {notification.createdAt.toLocaleTimeString("ru-RU", {
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </span>

                    <button
                        type="button"
                        className="notification-toast-AO__close"
                        onClick={handleClose}
                        title="Закрыть"
                    >
                        ×
                    </button>
                </div>
            </div>

            <div className="notification-toast-AO__body">
                {notification.message}
            </div>
        </div>
    );
};

export default NotificationToast;
